import React, { useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import type { Vehicle } from '../types/vehicle';
import EditorChrome from './ui/EditorChrome';
import Button from './ui/Button';
import {
  sanitizePublicSlugInput,
  sanitizePublicSlugTyping,
  suggestedDuplicateNickname,
  validatePublicSlug,
} from '../utils/vehicleUrls';

export type DuplicateSourceVehicle = {
  id: number;
  year?: number;
  make?: string;
  model?: string;
  nickname?: string | null;
  public_slug?: string | null;
  slug?: string | null;
};

type DuplicateVehicleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  vehicle: DuplicateSourceVehicle;
  ownerUsername: string;
  onDuplicated: (vehicle: Vehicle) => void;
};

export const DuplicateVehicleModal: React.FC<DuplicateVehicleModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  ownerUsername,
  onDuplicated,
}) => {
  const [nickname, setNickname] = useState('');
  const [publicSlug, setPublicSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sourceNickname = vehicle.nickname;
  const sourceYear = vehicle.year;
  const sourceMake = vehicle.make;
  const sourceModel = vehicle.model;

  useEffect(() => {
    if (!isOpen) return;
    const nextName = suggestedDuplicateNickname({
      nickname: sourceNickname,
      year: sourceYear,
      make: sourceMake,
      model: sourceModel,
    });
    setNickname(nextName);
    setPublicSlug(sanitizePublicSlugInput(nextName));
    setSlugError(null);
    setFormError(null);
    setLoading(false);
  }, [isOpen, sourceNickname, sourceYear, sourceMake, sourceModel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = nickname.trim();
    if (!name) {
      setFormError('Choose a name for the new build.');
      return;
    }
    const nextSlug = sanitizePublicSlugInput(publicSlug);
    if (nextSlug) {
      const err = validatePublicSlug(nextSlug);
      if (err) {
        setSlugError(err);
        return;
      }
    }
    setLoading(true);
    setFormError(null);
    try {
      const created = await apiService.duplicateVehicle(vehicle.id, {
        nickname: name,
        public_slug: nextSlug || undefined,
      });
      onDuplicated(created);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not duplicate this build.';
      setFormError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditorChrome
      variant="modal"
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate this build"
      description="Creates a new vehicle with this identity and current parts. Awards, contributors, and timeline stay on the original."
      size="md"
      closeOnOverlayClick={!loading}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="duplicate-vehicle-form" loading={loading} disabled={loading}>
            Duplicate build
          </Button>
        </div>
      }
    >
      <form id="duplicate-vehicle-form" onSubmit={handleSubmit} className="space-y-4">
        {formError ? (
          <p className="text-sm text-red-400" role="alert">
            {formError}
          </p>
        ) : null}

        <div>
          <label htmlFor="duplicate-nickname" className="input-label">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="duplicate-nickname"
            name="nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className="input"
            required
            maxLength={100}
            disabled={loading}
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="duplicate-public-slug" className="input-label">
            Build URL
          </label>
          <div className="flex items-stretch rounded-lg border border-gray-600 bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-orange-500/50">
            <span className="hidden sm:inline-flex items-center px-3 text-xs text-gray-400 font-mono whitespace-nowrap border-r border-gray-600">
              /{ownerUsername}/
            </span>
            <input
              id="duplicate-public-slug"
              name="public_slug"
              type="text"
              value={publicSlug}
              onChange={(e) => {
                setPublicSlug(sanitizePublicSlugTyping(e.target.value));
                if (slugError) setSlugError(null);
              }}
              onBlur={() => {
                const finalized = sanitizePublicSlugInput(publicSlug);
                if (finalized !== publicSlug) setPublicSlug(finalized);
                if (!finalized) {
                  setSlugError(null);
                  return;
                }
                setSlugError(validatePublicSlug(finalized));
              }}
              className="input border-0 rounded-none flex-1 font-mono focus:ring-0"
              placeholder="my-build"
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
              aria-invalid={Boolean(slugError)}
              aria-describedby="duplicate-public-slug-help"
            />
          </div>
          <p id="duplicate-public-slug-help" className="mt-2 text-xs text-gray-400">
            Preview:{' '}
            <span className="font-mono text-gray-300">
              /{ownerUsername}/{sanitizePublicSlugInput(publicSlug) || '…'}
            </span>
            . Leave blank to generate one from the name.
          </p>
          {slugError ? (
            <p className="mt-1 text-sm text-red-400" role="alert">
              {slugError}
            </p>
          ) : null}
        </div>
      </form>
    </EditorChrome>
  );
};

export default DuplicateVehicleModal;
